import Vue from 'vue'

import ApiService from '@/core/services/api.service'
import JwtService from '@/core/services/jwt.service'

import { LOGOUT } from './auth.module.js'
import router from '@/router'

import { eventBus } from '@/main'

// action types
export const GET_TARIFFS = 'getTariffs'
export const GET_TARIFF = 'getTariff'
export const SWITCH_TARIFF = 'switchTariff'
export const MANAGE_TARIFF = 'manageTariff'
export const ARCHIVE_TARIFF = 'archiveTariff'
export const RESTORE_TARIFF = 'restoreTariff'
export const MIGRATE_TARIFF_RESELLERS = 'migrateTariffResellers'

// mutation types
export const SAVE_TARIFFS = 'saveTariffs'
export const SAVE_TARIFF = 'saveTariff'
export const MARK_FOR_SWITCH_TARIFF = 'markForSwitchTariff'
export const UNMARK_FOR_SWITCH_TARIFF = 'unmarkForSwitchTariff'
export const SELECT_TARIFF = 'selectTariff'
export const UNSELECT_TARIFF = 'unselectTariff'
export const SAVE_MANAGE_TARIFF_ERRORS = 'saveManageTariffErrors'
export const CLEAR_MANAGE_TARIFF_ERRORS = 'clearManageTariffErrors'
export const SAVE_MIGRATION_TARGET_TARIFF = 'saveMigrationTargetTariff'
export const CLEAR_MIGRATION_TARGET_TARIFF = 'clearMigrationTargetTariff'

const SET_RESPONSE_STATE = 'setResponseState'
const TOAST_TARIFFS = 'showToast/SettingsTariffs'

export const DEFAULT_SELECTED_TARIFF = {
  _id: undefined,
  name: '',
  code: '',
  devices: 0,
  users: 0,
  total_referrals: 0,
  group: '',
  active: true,
  type: 'active',
  vpn_servers: [],
}

// returns tariff from allTariffs array based on passed id
function getTariff(tariffs, id) {
  return tariffs.filter(tariff => tariff._id === id)[0]
}

// Back-end GET/POST/PUT requests required and returned data in format that I was not comfortable to wotk with on front-end, so I've managed to transform it both at both receiving and sending
function convertForBackend(context) {
  const income = { ...context.state.selectedTariff }

  Object.keys(income).forEach(key => {
    if (key === 'active') {
      income.active = income.active ? 'on' : 'off'
    }

    if (key === 'group') {
      income.group_id = income.group
      delete income.group
    }

    if (key === 'type') {
      delete income.type
    }
  })

  return income
}

const state = {
  tariffs: {
    active: [],
    archieved: [],
  },
  markedForSwitchTariff: { ...DEFAULT_SELECTED_TARIFF },
  selectedTariff: { ...DEFAULT_SELECTED_TARIFF },
  selectedTariffId: undefined,
  migrationTargetTariff: { ...DEFAULT_SELECTED_TARIFF },
  manageTariffErrors: [],

  responses: {
    getTariffs: false,
    getTariff: false,
    switchTariff: false,
    manageTariff: false,
    archiveTariff: false,
    restoreTariff: false,
    migrateTariffResellers: false,
  },
}

const getters = {
  activeTariffs(state) {
    return state.tariffs.active
  },
  activeTariffsLength(state) {
    return state.tariffs.active.length
  },
  archievedTariffs(state) {
    return state.tariffs.archieved
  },
  archievedTariffsLength(state) {
    return state.tariffs.archieved.length
  },
  allTariffs(state) {
    return [...state.tariffs.active, ...state.tariffs.archieved]
  },
  enabledTarrifs: (state, getters) => id => {
    // this getter returns an array of active and enabled tariffs
    // plus one that is passed as id parameter
    const result = []

    const enabledTariffs = getters.activeTariffs.filter(tariff => tariff.active === 'on')
    if (enabledTariffs && enabledTariffs.length > 0) {
      enabledTariffs.forEach(tariff => {
        result.push(tariff)
      })
    }

    const target = getTariff(getters.allTariffs, id)
    if (target && target.active == 'off') {
      result.push(target)
    }

    result.sort((a, b) => {
      const nameA = a.name.toUpperCase()
      const nameB = b.name.toUpperCase()
      
      if (nameA < nameB) { return -1 }
      if (nameA > nameB) { return 1 }
      return 0
    })
    
    return result
  },
  activeTariffsEmpty(state) {
    return state.tariffs.active.length == 0
  },
  archievedEmpty(state) {
    return state.tariffs.archieved.length == 0
  },
  allTariffsLength(state, getters) {
    return getters.allTariffs.length
  },
  allTariffsEmpty(state, getters) {
    return getters.allTariffs.length === 0
  },
  tariffVpnServers: (state, getters) => id => {
    const target = getTariff(getters.allTariffs, id)
    return target.vpn_servers
  },
  tariffVpnServersEmpty: (state, getters) => id => {
    const target = getTariff(getters.allTariffs, id)
    return target.vpn_servers.length === 0
  },
  tariffIsActive: (state, getters) => id => {
    const target = getTariff(getters.allTariffs, id)
    return target.type === 'active'
  },
  tariffIsArchieved: (state, getters) => id => {
    const target = getTariff(getters.allTariffs, id)
    return target.type === 'archieved'
  },
  tariffGroupEmpty: (state, getters) => id => {
    const target = getTariff(getters.allTariffs, id)
    return target.group == null
  },
  markedForSwitchTariff(state) {
    return state.markedForSwitchTariff
  },
  selectedTariff(state) {
    return state.selectedTariff
  },
  selectedTariffId(state) {
    return state.selectedTariffId
  },
  selectedTariffExist(state, getters) {
    return getters.allTariffs.some(tariff => tariff._id == state.selectedTariffId)
  },
  tariffsModalTitle(state) {
    return state.selectedTariffId ? 'Edit tariff' : 'Add new tariff'
  },
  migrationTargetTariff(state) {
    return state.migrationTargetTariff
  },
  migrationTargetTariffEmpty(state) {
    return state.migrationTargetTariff._id === undefined
  },
  manageTariffErrors(state) {
    return state.manageTariffErrors
  },
  manageTariffError: state => field => {
    return state.manageTariffErrors.filter(err => err.field == field)
  },
  excludedActiveTariffs: (state, getters) => id => {
    return getters.activeTariffs.filter(tariff => tariff._id !== id && tariff.active === 'on')
  },
  tariffResellersEmpty: (state, getters) => id => {
    const target = getTariff(getters.allTariffs, id)
    return target.total_referrals === 0 || !target.total_referrals
  },
  tariffsResponses: state => type => {
    return state.responses[type]
  }
}

const actions = {
  [GET_TARIFFS](context, { type }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'getTariffs', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        let url
        if (type === 'active') {
          url = process.env.VUE_APP_TARIFFS
        } else if (type === 'archieved') {
          url = `${process.env.VUE_APP_TARIFFS}/deleted`
        }
        ApiService.get(url)
          .then(({ data }) => {
            context.commit(SAVE_TARIFFS, { tariffs: data , type })
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_TARIFFS, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'getTariffs', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [GET_TARIFF](context, { id, type }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'getTariff', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.get(`${process.env.VUE_APP_TARIFF}/${id}`)
          .then(({ data }) => {
            context.commit(SAVE_TARIFF, { data , type })
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_TARIFFS, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'getTariff', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [SWITCH_TARIFF](context) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'switchTariff', status: true })
        eventBus.$emit('startProgress')
        ApiService
          .patch(`${process.env.VUE_APP_TARIFF}/${state.markedForSwitchTariff._id}`, {
            active: state.markedForSwitchTariff.active === 'on' ? 'off' : 'on'
          })
          .then(({ data }) => {
            context.dispatch(GET_TARIFF, {
              id: state.markedForSwitchTariff._id,
              type: state.markedForSwitchTariff.type
            })
            eventBus.$emit(TOAST_TARIFFS, {
              message: data.message,
              variant: 'info'
            })
            resolve(data)
          })
          .catch(err  => {
            eventBus.$emit(TOAST_TARIFFS, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'switchTariff', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [MANAGE_TARIFF](context) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'manageTariff', status: true })
        eventBus.$emit('startProgress')

        const payload = convertForBackend(context)
        let method
        let url

        if (context.getters.selectedTariffExist) {
          method = 'put'
          url = `${process.env.VUE_APP_TARIFF}/${state.selectedTariffId}`
        } else {
          method = 'post'
          url = process.env.VUE_APP_TARIFF
        }

        ApiService[method](url, payload)
          .then(({ data }) => {
            if (context.getters.selectedTariffExist) {
              context.dispatch(GET_TARIFF, {
                id: state.selectedTariff._id,
                type: state.selectedTariff.type
              })
            }
            eventBus.$emit(TOAST_TARIFFS, {
              message: data.message,
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err  => {
            const error = err.response.data
            context.commit(SAVE_MANAGE_TARIFF_ERRORS, error.errors)
            eventBus.$emit(TOAST_TARIFFS, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'manageTariff', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [ARCHIVE_TARIFF](context, { id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'archiveTariff', status: true })
        eventBus.$emit('startProgress')

        ApiService.delete(`${process.env.VUE_APP_TARIFF}/${id}`)
          .then(({ data }) => {
            eventBus.$emit(TOAST_TARIFFS, {
              message: data.message,
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err => {
            const errors = err.response.data.errors
            if (errors) {
              for (let e = 0; e < errors.length; e++) {
                eventBus.$emit(TOAST_TARIFFS, {
                  message: errors[e].message,
                  variant: 'danger'
                })
              }
            }
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'archiveTariff', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [RESTORE_TARIFF](context, { id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'restoreTariff', status: true })
        eventBus.$emit('startProgress')

        ApiService.delete(`${process.env.VUE_APP_TARIFF}/deleted/${id}`)
          .then(({ data }) => {
            eventBus.$emit(TOAST_TARIFFS, {
              message: data.message,
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err => {
            const errors = err.response.data.errors
            if (errors) {
              for (let e = 0; e < errors.length; e++) {
                eventBus.$emit(TOAST_TARIFFS, {
                  message: errors[e].message,
                  variant: 'danger'
                })
              }
            }
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'restoreTariff', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [MIGRATE_TARIFF_RESELLERS](context, { currTariff, targetTariff }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'migrateTariffResellers', status: true })
        eventBus.$emit('startProgress')

        ApiService.patch(
            `${process.env.VUE_APP_TARIFF}/${currTariff._id}/items`,
            { change: targetTariff._id }
          )
          .then(({ data }) => {
            context.dispatch(GET_TARIFF, {
              id: currTariff._id,
              type: currTariff.type
            })
            context.dispatch(GET_TARIFF, {
              id: targetTariff._id,
              type: targetTariff.type
            })
            eventBus.$emit(TOAST_TARIFFS, {
              message: data.message,
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err  => {
            const errors = err.response.data.errors
            if (errors) {
              for (let e = 0; e < errors.length; e++) {
                eventBus.$emit(TOAST_TARIFFS, {
                  message: errors[e].message,
                  variant: 'danger'
                })
              }
            }
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'migrateTariffResellers', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
}

const mutations = {
  [SAVE_TARIFFS](state, { tariffs, type }) {
    const income = tariffs
    if (income.length > 0) {
      income.forEach(tariff => {
        tariff.type = type
      })
    }
    state.tariffs[type] = income
  },
  [SAVE_TARIFF](state, { data, type }) {
    const target = state.tariffs[type].filter(i => i._id === data._id)[0]
    if (target) {
      Object.keys(target).forEach(key => {
        Vue.set(target, key, data[key])
        Vue.set(target, 'type', type)
      })
    }
  },
  [MARK_FOR_SWITCH_TARIFF](state, tariff) {
    state.markedForSwitchTariff = tariff
  },
  [UNMARK_FOR_SWITCH_TARIFF](state) {
    state.markedForSwitchTariff = { ...DEFAULT_SELECTED_TARIFF }
  },
  [SELECT_TARIFF](state, recipe) {
    const income = { ...recipe }

    Object.keys(income).forEach(key => {
      // modify data to use it in bootstrap checkbox
      // it requires array with strings, not array with objects
      // https://bootstrap-vue.org/docs/components/form-checkbox
      if (key === 'vpn_servers') {
        if (income.vpn_servers.length > 0) {
          const moddedArr = []
          income.vpn_servers.forEach(el => {
            moddedArr.push(el._id)
          })
          income.vpn_servers = moddedArr
        }
      }

      // convert 'on' / 'off' String => Boolean to use in checkbox
      // https://bootstrap-vue.org/docs/components/form-checkbox
      if (key === 'active') {
        income.active = income.active === 'on'
      }

      // get group._id from group {} object to use it as v-model in b-form-radio-group
      // https://bootstrap-vue.org/docs/components/form-radio
      if (key === 'group') {
        income.group = income.group._id
      }
    })

    Object.keys(income).forEach(key => {
      Vue.set(state.selectedTariff, key, income[key])
    })

    state.selectedTariffId = income._id
  },
  [UNSELECT_TARIFF](state) {
    Object.keys(state.selectedTariff).forEach(key => {
      Vue.set(state.selectedTariff, key, DEFAULT_SELECTED_TARIFF[key])
    })
    state.selectedTariffId = null
  },
  [SAVE_MIGRATION_TARGET_TARIFF](state, tariff) {
    state.migrationTargetTariff = tariff
  },
  [CLEAR_MIGRATION_TARGET_TARIFF](state) {
    state.migrationTargetTariff = { ...DEFAULT_SELECTED_TARIFF }
  },
  [SAVE_MANAGE_TARIFF_ERRORS](state, errors) {
    state.manageTariffErrors = errors
  },
  [CLEAR_MANAGE_TARIFF_ERRORS](state) {
    state.manageTariffErrors = []
  },
  [SET_RESPONSE_STATE](state, { type, status }) {
    state.responses[type] = status
  },
}

export default {
  state,
  actions,
  mutations,
  getters,
}
